<template>
  <PageTemplate>
    <Loader v-if="!isLoaded"/>
    <div v-else>
      <div class="d-flex justify-content-between">
        <h6 class="element-header">
          Rechnungslegung
        </h6>

        <CreateButton
            label="Rechnung erstellen"
            :to="{ name: 'billingCoachingSelection' }">
        </CreateButton>
      </div>

      <div class="element-box">
        <h5 class="form-header mb-2">
          {{ $t('billing.latest_bills_list') }}
        </h5>

        <Menu/>

        <div class="row align-items-center justify-content-between mt-4">
          <div>
            <PerPage :values="[10, 25, 50, 100]" @update:perPage="getLatestBills"/>

            <ExcelExportButton
                style="margin-right: 15px;"
                :label="$t('general.excel_export')"
                :loading="isExcelLoaded"
                @click="getExcelExport"/>
          </div>

          <div class="form-group mb-0 col-12 col-md-4 mt-3 mt-md-0">
            <input
                name="searchTerm"
                @input="handleDebounceSearch" class="form-control"
                v-model="filterDebounce.searchTerm"
                :placeholder="$t('general.button.search')"/>
          </div>
        </div>

        <EmptyState
            v-if="!bills.data?.length"
            icon="fa-file-invoice"
            title="Keine Rechnungen gefunden"
            subtitle="Es wurden keine Rechnungen gefunden, die Ihren Suchkriterien entsprechen."/>

        <form v-else @submit.prevent="" method="post">
          <div class="table-responsive overflow-auto pt-4">
            <table class="table table-striped">
              <thead>
              <tr>
                <th></th>
                <th style="text-align: right">
                  <a href="#" @click="sortBy('billNumber')" style="cursor:pointer;">
                    Rechnungs-Nr.
                    <i :class="getIconClass('billNumber')"></i>
                  </a>
                </th>
                <th>
                  <a href="#"  @click="sortBy('date')" style="cursor:pointer;">
                    Rechnungsdatum
                    <i :class="getIconClass('date')"></i>
                  </a>
                </th>
                <th>Kundennummer</th>
                <th>
                  <a href="#"  @click="sortBy('unemployedFullName')" style="cursor:pointer;">
                    Kundenname
                    <i :class="getIconClass('unemployedFullName')"></i>
                  </a>
                </th>
                <th>
                  <a href="#" @click="sortBy('client')" style="cursor:pointer;">
                    Kostenträger
                    <i :class="getIconClass('client')"></i>
                  </a>
                </th>
                <th>Debitor</th>
                <th>
                  <a href="#" @click="sortBy('operationType')" style="cursor:pointer;">
                    Maßnahmentyp
                    <i :class="getIconClass('operationType')"></i>
                  </a>
                </th>
                <th>Abrechnungszeitraum</th>
                <th style="text-align: right">
                  <a href="#" @click="sortBy('totalUnits')" style="cursor:pointer;">
                    Sitzungen
                    <i :class="getIconClass('totalUnits')"></i>
                  </a>
                </th>
                <th style="text-align: right">
                  <a href="#" @click="sortBy('sum')" style="cursor:pointer;">
                    Rechnungsbetrag
                    <i :class="getIconClass('sum')"></i>
                  </a>
                </th>
                <th style="text-align: center">
                  <a href="#"  @click="sortBy('status')" style="cursor:pointer;">
                    Status
                    <i :class="getIconClass('status')"></i>
                  </a>
                </th>
                <th></th>
                <!--                <th></th>-->
                <th></th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="bill in bills.data" :key="bill.id" class="border">
                <td class="text-center">
                  <label class="custom-checkbox position-relative">
                    <input type="checkbox"
                           v-model="selectedBillIds"
                           :value="bill.id"
                           class="custom-checkbox position-absolute"/>
                    <div class="custom-check-icon"></div>
                  </label>
                </td>
                <td>{{ bill.billNumber }}</td>
                <td>{{ bill.invoiceDate }}</td>
                <td>
                  {{
                    bill.coachingType === 'socio_therapy_coaching' ?
                        bill.unemployed?.insuranceNumber ||
                        '' : bill.unemployed?.aaNumber || ''
                  }}
                </td>
                <td>{{ bill?.unemployed?.firstName }} {{ bill?.unemployed?.lastName }}</td>
                <td>
                  {{ bill.client?.name || bill.receiver }}
                </td>
                <td>
                  {{ bill.client?.debtor }}
                </td>
                <td><span v-if="bill.coachingType !== 'special_case'">{{ bill.operationTypeTranslation }}</span></td>
                <td>{{ bill.billingPeriod }}</td>
                <td class="text-right">
                  {{ bill.totalUnits }} <span v-if="bill.billingType === 'pauschalabrechnung'">%</span>
                </td>
                <td class="text-right">{{ bill.sum }}</td>
                <td style="min-width: 160px">
                  <div class="pl-3 flex items-center space-x-2">
                    <div
                        class="status-pill p-2 mr-2 rounded-full"
                        :class="{
                        'green': bill.status === 'billed' || bill.status === 'approved',
                        'yellow': bill.status === 'pending',
                        'red': !['billed', 'approved', 'pending'].includes(bill.status),
                      }"
                        :title="'invoice_status.' + bill.status"></div>
                    <span>{{ bill.statusTranslation }}</span>
                  </div>
                </td>
                <!--                <td>-->
                <!--                  <BillCorrectionButton :bill="bill"/>-->
                <!--                </td>-->
                <td style="padding-right: 0">
                  <CancelButton
                      :bill="bill"
                      @billCancellation="cancellationBillModal"/>
                </td>
                <td>
                  <PrintButton :bill="bill"/>
                </td>
              </tr>
              </tbody>
            </table>
          </div>

          <div class="row mt-3 d-flex align-items-end">
            <div class="col-lg-6 col-md-6 col-sm-12 d-flex justify-content-start pl-4 mb-4">
              <DownloadButton
                  :label="'Rechnung download'"
                  :loading="isBillDownloaded"
                  @click="downloadBills"/>
            </div>
<!--            <div class="col-lg-6 col-md-6 col-sm-12 d-flex justify-content-start pl-4 mb-4">-->
<!--              <button type="submit"-->
<!--                      @click.prevent="downloadBills"-->
<!--                      class="btn btn-primary">-->
<!--                 <span v-if="isBillDownloaded" class="spinner-border spinner-border-sm mr-2" role="status"-->
<!--                       aria-hidden="true"></span>-->
<!--                <span v-if="!isBillDownloaded">  Rechnung download</span>-->
<!--                <span v-else>{{ $t('general.button.generating_document') }}</span>-->
<!--              </button>-->
<!--            </div>-->

            <div class="col-lg-6 col-md-6 col-sm-12 d-flex justify-content-end">
              <Pagination
                  v-if="bills?.pagination?.total_pages > 1"
                  :totalPages="bills?.pagination?.total_pages"
                  :currentPage="bills?.pagination?.current_page"
                  :totalItems="bills?.pagination?.total_items"
                  :perPage="bills?.pagination?.items_per_page"
                  @pagechanged="getLatestBills"/>
            </div>
          </div>
        </form>

      </div>
    </div>

    <ModalConfirm
        v-model="showCancellationBillModal"
        confirmationQuestion="Möchten Sie diese Rechnung wirklich stornieren?"
        :header="approveHeader"
        :onConfirm="confirmBillCancellation"/>
  </PageTemplate>
</template>

<script setup>
import billingService from '@/api/Billing';
import CreateButton from '@/components/CreateButton.vue';
import EmptyState from '@/components/EmptyState.vue';
import Loader from '@/components/Loader.vue';
import ModalConfirm from '@/components/modal/ModalConfirm.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import Pagination from '@/components/Pagination.vue';
import PerPage from '@/components/PerPage.vue';
import {CloseToast, ErrorToast, SuccessToast} from '@/components/VueToast';
import {downloadFile, getSortIconClass} from '@/shared/functions';
import CancelButton from '@/views/App/Billing/Existing/CancelButton.vue';
import Menu from '@/views/App/Billing/Existing/Menu.vue';
import PrintButton from '@/views/App/Billing/Existing/PrintButton.vue';
import debounce from 'lodash/debounce';
import {onMounted, ref, watch} from 'vue';
import {useI18n} from 'vue-i18n';
import ExcelExportButton from "@/components/ExcelExportButton.vue";
import DownloadButton from "@/components/button/DownloadButton.vue";

const {t} = useI18n();
const bills = ref([]);
const selectedBillIds = ref([]);
const isSearching = ref(false);
const showCancellationBillModal = ref(false);
const approveHeader = ref('');
const isLoaded = ref(false);
const billId = ref('');
const billType = ref('');
const submitForm = ref(false);
const isExcelLoaded = ref(false);
const isBillDownloaded = ref(false)

const filterDebounce = ref({
  searchTerm: '',
  orderBy: 'DESC',
  sortBy: '',
});

watch(submitForm, () => {
  billId.value = '';
  billType.value = '';
  showCancellationBillModal.value = false;
});

const debouncedSearch = debounce(async (query) => {
  await getLatestBills(1, undefined, filterDebounce.value);
}, 600);

const handleDebounceSearch = (event) => {
  debouncedSearch(event.target.value);
};

async function getLatestBills(page = 1, perPage, passedFilters) {
  CloseToast();

  if (passedFilters) {
    isSearching.value = true;
  }

  const perPageRes = bills.value?.pagination?.items_per_page;

  try {
    const {data} = await billingService.getLatestBills({
      page: page,
      limit: perPage ?? perPageRes,
      ...(passedFilters ?? passedFilters),
    });

    bills.value = data;
    isSearching.value = false;
  } catch (error) {
    await ErrorToast(error);
    isSearching.value = false;
  }
}

function cancellationBillModal(passedBillId, passedBillType) {
  billId.value = '';
  billType.value = '';

  showCancellationBillModal.value = true;

  billId.value = passedBillId;
  billType.value = passedBillType;
}

async function confirmBillCancellation() {
  showCancellationBillModal.value = false;

  try {
    if (billType.value === 'private_coaching') {
      await billingService.privateCoachingBillCancellation(billId.value);
    } else if (billType.value === 'group_coaching') {
      await billingService.groupCoachingBillCancellation(billId.value);
    } else if (billType.value === 'socio_therapy_coaching') {
      await billingService.socioTherapyCoachingBillCancellation(billId.value);
    } else if (billType.value === 'assisted_living_coaching') {
      await billingService.assistedLivingCoachingBillCancellation(billId.value);
    } else if (billType.value === 'case_management_coaching') {
      await billingService.caseManagementCoachingBillCancellation(billId.value);
    } else if (billType.value === 'private_learning_coaching') {
      await billingService.privateLearningCoachingBillCancellation(billId.value);
    } else if (billType.value === 'special_case') {
      await billingService.specialCaseBillCancellation(billId.value);
    } else {
      await ErrorToast('Invalid bill type !!!!!!');
    }

    submitForm.value = false;
    SuccessToast(t('general.toasts.save_success'));
    await getLatestBills();
  } catch (error) {
    await ErrorToast(error);
  }

}

function getIconClass(key) {
  return getSortIconClass(filterDebounce.value, key);
}

function sortBy(key) {
  filterDebounce.value = {
    ...filterDebounce.value,
    sortBy: key,
    orderBy: filterDebounce.value.orderBy === 'DESC' || filterDebounce.value.sortBy !== key ? 'ASC' : 'DESC',
  };
  getLatestBills(1, undefined, filterDebounce.value);
}

async function downloadBills() {
  CloseToast();

  try {
    if (selectedBillIds.value.length === 0) {
      await ErrorToast({
        response: {
          data: {
            error: {
              message: 'Rechnung zum Herunterladen auswählen!'
            }
          }
        }
      });
    } else {
      await downloadFile(
          '/billing/latest-bills/download',
          undefined,
          {billIds: selectedBillIds.value},
          false,
          'POST',
          isBillDownloaded
      );
      selectedBillIds.value = [];
    }
  } catch (error) {
    await ErrorToast(error);
  }
}


async function getExcelExport() {
  await downloadFile(
      '/billing/latest-bills/excel-export',
      undefined,
      { searchTerm: filterDebounce.value.searchTerm},
      false,
      'GET', isExcelLoaded)
}

onMounted(async () => {
  await Promise.all([
    getLatestBills(),
  ]);

  isLoaded.value = true;
});

</script>

<style scoped>
.custom-checkbox .custom-check-icon {
  position: relative;
  display: inline-block;
  width: 18px;
  min-width: 18px;
  height: 18px;
  min-height: 18px;
  border: 2px solid var(--primary);
  border-radius: 4px;
  background-color: white;
  margin-right: 8px;
}

.custom-checkbox .custom-check-icon::before {
  content: '';
  position: absolute;
  top: 1px;
  left: 4px;
  width: 6px;
  height: 11px;
  border: solid var(--primary);
  border-width: 0 3px 3px 0;
  transform: rotate(45deg);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.custom-checkbox input[type="checkbox"]:checked + .custom-check-icon::before {
  opacity: 1;
}

.custom-checkbox .custom-check-icon:hover {
  border-color: var(--primary);
}
</style>